import React from "react";
import NextHead from "next/head";
import { isDevEnv } from "../../../../../utils/env";
import { getHostname } from "../../../../../core/helpers";
import { getTotalPages } from "../ProductList/helpers";
import { getCanonicalUrl, getNextPageUrl, getPrevPageUrl, removePageFromUrl } from "./helpers";
import { useAppContext } from "../../../../../lib/appContext";
import { useRouter } from "../../../../../lib/routerContext";
import { useProductsContext } from "../state/products";
import { useFilterParams } from "../state/products/items/hooks";
import { SECOND_PAGE, MAX_INDEXABLE_PAGES } from "../../Pagination/Pagination/constants";
import { FRONTEND_BU, NEXT_BU } from "../state/products/items/constants";
import { useFilters } from "../../../context/filters";
import { useUrlFilters } from "../../../context/selectedFilters";

export const Head: React.FC = () => {
  const { countryCode, businessUnitConfig } = useAppContext();
  const router = useRouter();
  const { totalResults, page } = useProductsContext();
  const filters = useFilterParams();
  const validFilters = useFilters();
  const currentFilters = useUrlFilters();

  const hostname = isDevEnv ? "http://dummy" : getHostname(countryCode);
  const currentPath = router && router.asPath ? router.asPath : "";
  const totalPages = getTotalPages(totalResults);

  const hasProducts = !!totalResults && totalResults > 0;
  const hasExceededFilters = filters.length > businessUnitConfig.maxIndexableFilters;
  const secondaryFilters = validFilters.entityFilters!.filter(filter => !filter.featured);
  const validFiltersList = secondaryFilters.map(generalFilter =>
    generalFilter.filters.map(
      specificFilter => `${generalFilter.entity.slug}=${specificFilter.slug}`
    )
  );
  const orderedCurrentFilters = Object.keys(currentFilters.secondary).map(
    key => `${key}=${currentFilters.secondary[key]}`
  );
  const canonicalUrl = getCanonicalUrl(
    hostname,
    currentPath,
    validFiltersList,
    orderedCurrentFilters
  );
  const invalidBusinessUnit =
    !!router &&
    !!router.query &&
    !!router.query.business &&
    (router.query.business === FRONTEND_BU || router.query.business === NEXT_BU);

  const noIndex =
    !hasProducts || hasExceededFilters || page > MAX_INDEXABLE_PAGES || invalidBusinessUnit;
  const canonical = hasProducts && !hasExceededFilters && !noIndex;

  return (
    <NextHead>
      {totalPages > 1 && !noIndex && (
        <>
          {page === SECOND_PAGE && <link rel="prev" href={removePageFromUrl(canonicalUrl)} />}
          {page > SECOND_PAGE && <link rel="prev" href={getPrevPageUrl(canonicalUrl)} />}
          {page < Math.min(totalPages, MAX_INDEXABLE_PAGES) && (
            <link rel="next" href={getNextPageUrl(canonicalUrl)} />
          )}
        </>
      )}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      {canonical && <link rel="canonical" href={removePageFromUrl(canonicalUrl)} />}
    </NextHead>
  );
};
